import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Users, Download, Search, Filter,
  ArrowLeft, DollarSign, Phone, Mail, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CautionMoneyDue() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({
    course_id: '',
    search: ''
  });
  const [summary, setSummary] = useState({
    total_students: 0,
    total_due: 0
  });

  useEffect(() => {
    fetchCourses();
    fetchPendingStudents();
  }, []);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/master-settings/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchPendingStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caution-money/pending-students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data.pending_students || []);
        
        // Calculate summary
        const totalDue = data.pending_students?.reduce((sum, s) => sum + (s.pending_amount || 0), 0) || 0;
        setSummary({
          total_students: data.total_count || 0,
          total_due: totalDue
        });
      }
    } catch (error) {
      console.error('Error fetching pending students:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter students
  const filteredStudents = students.filter(student => {
    if (filters.course_id && student.course_id !== filters.course_id) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        student.full_name?.toLowerCase().includes(searchLower) ||
        student.student_id?.toLowerCase().includes(searchLower) ||
        student.admission_number?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const exportToCSV = () => {
    if (!filteredStudents.length) return;

    const headers = ['Student ID', 'Name', 'Course', 'Required Amount', 'Paid Amount', 'Pending Amount', 'Phone'];
    const rows = filteredStudents.map(s => [
      s.student_id || s.admission_number,
      s.full_name,
      s.course_name,
      s.required_caution_money,
      s.paid_amount,
      s.pending_amount,
      s.phone || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caution_money_due_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const collectPayment = (studentId) => {
    navigate(`/fees/caution-money/pay?student=${studentId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/fees/caution-money/report')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Caution Money Due Report</h1>
            <p className="text-gray-600 mt-1">Students who haven't paid caution money</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPendingStudents}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV} disabled={!filteredStudents.length}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.total_students}</p>
                <p className="text-sm text-gray-600">Students with Due</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">₹{summary.total_due?.toLocaleString('en-IN')}</p>
                <p className="text-sm text-gray-600">Total Due Amount</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{filteredStudents.length}</p>
                <p className="text-sm text-gray-600">Filtered Results</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Search by name or ID..."
                />
              </div>
            </div>

            <div className="w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <select
                value={filters.course_id}
                onChange={(e) => setFilters(prev => ({ ...prev, course_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">All Courses</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>

            <Button 
              variant="outline" 
              onClick={() => setFilters({ course_id: '', search: '' })}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Students with Pending Caution Money
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="text-lg font-medium text-green-600">No pending caution money!</p>
              <p className="text-sm">All students have paid their caution money</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="due-students-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Student</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Course</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Required</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Paid</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Pending</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="border-b hover:bg-red-50">
                      <td className="p-3">
                        <p className="font-medium">{student.full_name}</p>
                        <p className="text-sm text-gray-500">{student.student_id || student.admission_number}</p>
                      </td>
                      <td className="p-3 text-gray-600">{student.course_name}</td>
                      <td className="p-3 text-right font-medium">
                        ₹{student.required_caution_money?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right text-green-600">
                        ₹{student.paid_amount?.toLocaleString('en-IN') || 0}
                      </td>
                      <td className="p-3 text-right font-bold text-red-600">
                        ₹{student.pending_amount?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm"
                          onClick={() => collectPayment(student.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`collect-btn-${student.id}`}
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          Collect
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="4" className="p-3 text-right">Total Pending:</td>
                    <td className="p-3 text-right text-red-600 text-lg">
                      ₹{filteredStudents.reduce((sum, s) => sum + (s.pending_amount || 0), 0).toLocaleString('en-IN')}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Button onClick={() => navigate('/fees/caution-money/pay')} className="bg-green-600 hover:bg-green-700">
          <DollarSign className="h-4 w-4 mr-2" /> Collect Caution Money
        </Button>
        <Button variant="outline" onClick={() => navigate('/fees/caution-money/report')}>
          View Full Report
        </Button>
      </div>
    </div>
  );
}
